import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Suspense, lazy, useEffect, useState } from "react";
import usePaywallPosters from "@/modules/PaywallModal/hooks/usePaywallPosters";
import { AmplitudeTrackingEvent } from "@/types/amplitude-tracking.type";
import { useTracking } from "@/components/ClientAnalytics";
import usePremium from "@/hooks/usePremium";
import CatalogLayoutModule from "@/modules/CatalogLayout";
import { useAppStore } from "@/stores/useApp";
import { useAppStateStore } from "@/stores/useAppState";

const PaywallModal = lazy(() => import("@/modules/PaywallModal"));

/** Tracks magic-link auth so remounts (e.g. React Strict Mode) can recover. */

export const Route = createFileRoute("/_default/")({
  component: IndexRoute,
  validateSearch: ({
    paywall,
    upsell,
  }: {
    paywall?: string;
    upsell?: string | number;
  }) => ({
    ...(paywall === "default" ? { paywall: "default" as const } : {}),
    ...(String(upsell) === "1" ? { upsell: "1" as const } : {}),
  }),
});

const HomePaywall = () => {
  const navigate = useNavigate();
  const { paywall } = Route.useSearch();
  const { grantPremiumAccess } = usePremium();
  const { trackAmplitudeEvent } = useTracking();

  const hasPaywallParams = paywall === "default";

  // No series context on the home: the top four alone.
  const posters = usePaywallPosters(null, { skip: !hasPaywallParams });

  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const clearPaywallSearchParams = () => {
    navigate({ to: "/", search: {}, replace: true });
  };

  useEffect(() => {
    if (hasPaywallParams)
      trackAmplitudeEvent(AmplitudeTrackingEvent.showPaywall, {
        "series name": "",
        "episode number": "",
        products: [""],
        "default product": "",
        origin: window.location.pathname,
        template: "reengage",
      });

    setIsPaywallOpen(hasPaywallParams);
  }, []);

  if (!isPaywallOpen) return null;

  return (
    <Suspense fallback={null}>
      <PaywallModal
        purchaseType="reengage"
        paywallSource="initial"
        onPurchaseComplete={() => {
          grantPremiumAccess();
        }}
        onDismiss={() => {
          useAppStore.setState({ initialPaywallDismissed: true });
          clearPaywallSearchParams();
          setIsPaywallOpen(false);
        }}
        onClose={() => {
          clearPaywallSearchParams();
          setIsPaywallOpen(false);
        }}
        featuredShows={posters}
        showTitle=""
      />
    </Suspense>
  );
};

/** Dev/standalone entry: `?upsell=1` flips the store flag (global render lives in __root). */
const HomeUpsell = () => {
  const navigate = useNavigate();
  const openUpsell = useAppStateStore((s) => s.openUpsell);
  const { upsell } = Route.useSearch();

  useEffect(() => {
    if (upsell === "1") {
      openUpsell();
      navigate({ to: "/", search: {}, replace: true });
    }
  }, []);

  return null;
};

function IndexRoute() {
  const { isPremiumUser } = usePremium();

  return (
    <>
      {!isPremiumUser && <HomePaywall />}
      <HomeUpsell />
      <CatalogLayoutModule />
    </>
  );
}
